import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { initTelemetry, ErrorBoundary } from '@/lib/telemetry';
import { initAnalytics } from '@/lib/analytics';
import { API_BASE } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { getConsent, subscribe as subscribeConsent } from '@/lib/cookie-consent';
import App from './App';
import './index.css';

// Initialize telemetry before rendering. Error/performance telemetry
// runs under legitimate interest for security and operational integrity
// and is NOT gated by the cookie banner.
initTelemetry();

// Platform usage analytics is "optional" under the cookie banner — only
// initialize once the user has accepted. If they decline or haven't
// decided, we skip it; subscribing below picks up a later acceptance.
let analyticsInitialized = false;
function maybeInitAnalytics() {
  if (analyticsInitialized) return;
  if (getConsent().status !== 'accepted') return;
  analyticsInitialized = true;
  // apiBase MUST match the rest of the app (api.ts API_BASE) so analytics
  // POSTs route to the API origin — VITE_API_URL in prod, /v1 with Vite
  // proxy locally and in Codespaces. The user_id provider is wired later
  // when the auth context settles; calling init at consent time is
  // intentional so anonymous events on pre-login pages still get captured.
  initAnalytics({ apiBase: API_BASE });
}
maybeInitAnalytics();
subscribeConsent(maybeInitAnalytics);

function FallbackError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-foreground mb-4">Something went wrong</h1>
        <p className="text-muted-foreground mb-6">Please try refreshing the page.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Refresh page
        </button>
      </div>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary fallback={<FallbackError />}>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
);
